import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, Box, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/en-gb";
import { GoogleMap, Polyline, Marker } from "@react-google-maps/api";
import DateTimeRangePicker from "@wojtekmaj/react-datetimerange-picker";
import "@wojtekmaj/react-datetimerange-picker/dist/DateTimeRangePicker.css";
import "react-calendar/dist/Calendar.css";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Environment variables
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("en-gb");

export default function DeviceRouteMap({ deviceId, chassisNum }) {
	const now = dayjs();

	const [startDT, setStartDT] = useState(now.subtract(1, "hour"));
	const [endDT, setEndDT] = useState(now);
	const [points, setPoints] = useState([]); // {lat, lng, time}
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	// Maximum range validation: 24 hours in milliseconds
	const maxRangeMs = 24 * 60 * 60 * 1000;
	const rangeMs = endDT.diff(startDT);
	const isRangeValid = rangeMs >= 0 && rangeMs <= maxRangeMs;

	const formatWithOffset = (d) => {
		// include full datetime with offset
		const jsDate = d.toDate();
		const pad = (n) => String(n).padStart(2, "0");
		const tzOffsetMinutes = -jsDate.getTimezoneOffset();
		const sign = tzOffsetMinutes >= 0 ? "+" : "-";
		const absOffset = Math.abs(tzOffsetMinutes);
		const hours = pad(Math.floor(absOffset / 60));
		const minutes = pad(absOffset % 60);
		const localIso = d.format("YYYY-MM-DDTHH:mm:ss");
		return `${localIso}${sign}${hours}:${minutes}`;
	};

	const fetchRoute = useCallback(async () => {
		if (!isRangeValid) {
			setError("Please select a range of 24 hours or less.");
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const fromParam = formatWithOffset(startDT);
			const toParam = formatWithOffset(endDT);
			const url = `${BACKEND_URL}devices/${deviceId}/route/?from=${encodeURIComponent(
				fromParam
			)}&to=${encodeURIComponent(toParam)}`;

			const resp = await fetch(url, {
				headers: {
					Accept: "application/json",
					Authorization: `Bearer ${BACKEND_TOKEN}`,
				},
			});
			if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
			const json = await resp.json();
			// include time
			const pts = (json.data || []).map((p) => ({
				lat: p.latitude,
				lng: p.longitude,
				time: dayjs(p.time),
			}));
			setPoints(pts);
		} catch (e) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	}, [startDT, endDT, deviceId, isRangeValid]);

	useEffect(() => {
		fetchRoute();
	}, [fetchRoute]);

	// Colored marker icons for start (green) and end (red)
	const startIcon = {
		url: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
	};
	const endIcon = {
		url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
	};

	const segments = useMemo(() => {
		if (!Array.isArray(points) || points.length < 2) return [];
		const t0 = points[0].time;
		const tN = points[points.length - 1].time;
		const totalMs = tN.diff(t0);

		const segs = [];
		for (let i = 1; i < points.length; i++) {
			const p0 = points[i - 1],
				p1 = points[i];
			if (
				!Number.isFinite(p0.lat) ||
				!Number.isFinite(p0.lng) ||
				!Number.isFinite(p1.lat) ||
				!Number.isFinite(p1.lng)
			)
				continue;

			const ratio = totalMs > 0 ? p1.time.diff(t0) / totalMs : 0;
			const hue = Math.round(120 - ratio * 120);
			const color = `hsl(${hue},100%,50%)`;

			segs.push({
				path: [
					{ lat: p0.lat, lng: p0.lng },
					{ lat: p1.lat, lng: p1.lng },
				],
				color,
			});
		}
		return segs;
	}, [points]);

	return (
		<Card sx={{ mb: 2, overflow: "visible" }}>
			<Typography variant="subtitle1">
				{`Route History - `}
				<Typography variant="caption">{chassisNum}</Typography>
			</Typography>
			{/* <CardHeader title={`Route History - ${chassisNum}`} /> */}
			<CardContent
				sx={{ overflow: "visible", minHeight: { xs: 540, sm: 500 } }}
			>
				<LocalizationProvider
					dateAdapter={AdapterDayjs}
					adapterLocale="en-gb"
				>
					<Box
						sx={{
							mb: 2,
							// scope all styling to this instance only
							"& .tm-range-picker": { width: "100%" },
							"& .tm-range-picker .react-datetimerange-picker__wrapper":
								{
									display: "flex",
									alignItems: "center",
									gap: 1,
									flexDirection: { xs: "column", sm: "row" },
								},
							"& .tm-range-picker .react-datetimerange-picker__range-divider":
								{
									px: 1,
									color: "text.secondary",
									display: { xs: "none", sm: "inline" },
								},
							"& .tm-range-picker .react-datetimerange-picker__inputGroup":
								{
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1.5,
									bgcolor: "background.paper",
									px: 1.5,
									py: 1,
									width: "100%",
								},
							// keep popover visible even in short parents
							"& .react-calendar": {
								zIndex: 1500,
								position: "relative",
							},
						}}
					>
						<DateTimeRangePicker
							className="tm-range-picker"
							onChange={(range) => {
								if (!range) return;
								const [start, end] = range;
								if (start) setStartDT(dayjs(start));
								if (end) setEndDT(dayjs(end));
							}}
							value={[startDT.toDate(), endDT.toDate()]}
							disableClock
							clearIcon={null}
							maxDetail="minute"
							format={
								isMobile ? "dd/MM HH:mm" : "dd/MM/yyyy HH:mm"
							}
							dayPlaceholder="dd"
							monthPlaceholder="mm"
							yearPlaceholder={isMobile ? "yy" : "yyyy"}
							hourPlaceholder="hh"
							minutePlaceholder="mm"
							maxDate={new Date()}
						/>
					</Box>
				</LocalizationProvider>

				{!isRangeValid && (
					<Typography color="error" sx={{ mb: 2 }}>
						The selected range must be 24 hours or less.
					</Typography>
				)}

				{loading ? (
					<Typography>Loading route...</Typography>
				) : error ? (
					<Typography color="error">Error: {error}</Typography>
				) : points.length > 0 ? (
					<Box sx={{ height: 400, width: "100%" }}>
						<GoogleMap
							mapContainerStyle={{
								height: "100%",
								width: "100%",
							}}
							center={points[0] ?? { lat: 22.5726, lng: 88.3639 }}
							zoom={13}
						>
							{segments.length > 0 &&
								segments.map((seg, idx) => (
									<Polyline
										key={idx}
										path={seg.path}
										options={{
											strokeWeight: 4,
											strokeColor: seg.color,
										}}
									/>
								))}
							{points.length > 0 && (
								<Marker
									position={{
										lat: points[0].lat,
										lng: points[0].lng,
									}}
									icon={startIcon}
								/>
							)}
							{points.length > 1 && (
								<Marker
									position={{
										lat: points[points.length - 1].lat,
										lng: points[points.length - 1].lng,
									}}
									icon={endIcon}
								/>
							)}
						</GoogleMap>
					</Box>
				) : (
					// Placeholder keeps parent tall so the picker isn’t cut off
					<Box
						sx={{
							height: 400,
							width: "100%",
							border: "1px dashed",
							borderColor: "divider",
							borderRadius: 1,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							color: "text.secondary",
						}}
					>
						<Typography>No route data for this range.</Typography>
					</Box>
				)}
			</CardContent>
		</Card>
	);
}
